import { PricingBasis, PricingSheetRow } from '../../../shared/types';
import { countRemovalDayPrice } from '../../../shared/countRemovalDayPrice';
import { countRemovalMonthPrice } from '../../../shared/countRemovalMonthPrice';
import { roundToTwoDecimal } from '../../../shared/roundToTwoDecimal';

import { PRICE_FACTOR } from '../../../shared/constants';

import { countPriceCoefficient } from '../../../shared/utils/countPriceCoefficient';

export const COEFFICIENT_LIMIT_LIGHT = 1.2; // orange rows
export const COEFFICIENT_LIMIT = 1; // red rows
export const DISCOUNT_LIMIT = 0.7; // pink rows

export const WARNING_THRESHOLD = 5;

export const getCoefficient = (
  price: number | null | undefined,
  removalPrice: number | null,
) => {
  return removalPrice
    ? roundToTwoDecimal(Number(price) / removalPrice)
    : null;
};

export const getRemovalDayPrice = (row: PricingSheetRow) =>
  countRemovalDayPrice(
    Number(row.rentalReadyPrice),
    Number(row.depreciationPeriod),
    Number(row.targetUtilRate) / 100,
  );

export const getRemovalMonthPrice = (row: PricingSheetRow) =>
  countRemovalMonthPrice(
    Number(row.rentalReadyPrice),
    Number(row.depreciationPeriod),
    Number(row.targetUtilRate) / 100,
  );

export const getProposalDayCoefficients = (rows: PricingSheetRow[]) =>
  rows.map((row) => {
    const removalDayPrice = countRemovalDayPrice(
      Number(row?.rentalReadyPrice),
      Number(row?.depreciationPeriod),
      Number(row?.targetUtilRate) / 100,
    );

    if (row?.proposalDayPrice && removalDayPrice) {
      const priceCoefficientDay = countPriceCoefficient(
        row?.proposalDayPrice / PRICE_FACTOR,
        removalDayPrice,
      );
      return priceCoefficientDay;
    }
    return null;
  });

export const getProposalMonthCoefficients = (
  rows: PricingSheetRow[],
) =>
  rows.map((row) => {
    const removalMonthPrice = countRemovalMonthPrice(
      Number(row?.rentalReadyPrice),
      Number(row?.depreciationPeriod),
      Number(row?.targetUtilRate) / 100,
    );

    if (row?.proposalMonthPrice && removalMonthPrice) {
      const priceCoefficientMonth = countPriceCoefficient(
        row?.proposalMonthPrice / PRICE_FACTOR,
        removalMonthPrice,
      );
      return priceCoefficientMonth;
    }
    return null;
  });

const getCoefficientsSummed = (coefficients: (number | null)[]) =>
  coefficients.reduce((current, newCoefficient) => {
    if (current !== null && newCoefficient !== null) {
      return current + newCoefficient;
    }
    return current;
  }, 0);

export const getCoefficientsMeanValue = (
  coefficients: (number | null)[],
) => {
  const sum = getCoefficientsSummed(coefficients);
  return sum ? sum / coefficients.length : null;
};

export const getCoefficientsMeanWeighted = (
  rows: PricingSheetRow[],
  pricingBasis: PricingBasis,
) => {
  const values = rows.reduce<{
    weightSum: number;
    estimatedYieldSum: number;
  }>(
    (acc, cur) => {
      const coefficient =
        (pricingBasis === 'DAY'
          ? getDayCoefficient(cur)
          : getMonthCoefficient(cur)) ?? 0;
      const priceKey =
        pricingBasis === 'DAY' ? 'dayPrice' : 'monthPrice';
      const rentalDaysKey =
        pricingBasis === 'DAY' ? 'rentalDaysDay' : 'rentalDaysMonth';
      const maybeEstimatedYield =
        ((Number(cur[priceKey]) ?? 0) / PRICE_FACTOR) *
        (Number(cur[rentalDaysKey]) ?? 0);
      const estimatedYield = isNaN(maybeEstimatedYield)
        ? 0
        : maybeEstimatedYield;
      return {
        weightSum: acc.weightSum + coefficient * estimatedYield,
        estimatedYieldSum: acc.estimatedYieldSum + estimatedYield,
      };
    },
    { weightSum: 0, estimatedYieldSum: 0 },
  );
  return values.weightSum / values.estimatedYieldSum;
};

export const getDayCoefficients = (rows: PricingSheetRow[]) =>
  rows.map(getDayCoefficient);

const getDayCoefficient = (row: PricingSheetRow) => {
  const removalDayPrice = countRemovalDayPrice(
    Number(row?.rentalReadyPrice),
    Number(row?.depreciationPeriod),
    Number(row?.targetUtilRate) / 100,
  );

  if (row?.dayPrice && removalDayPrice) {
    const priceCoefficientDay = countPriceCoefficient(
      row?.dayPrice / PRICE_FACTOR,
      removalDayPrice,
    );
    return priceCoefficientDay;
  }
  return null;
};

export const getMonthCoefficients = (rows: PricingSheetRow[]) =>
  rows.map(getMonthCoefficient);

const getMonthCoefficient = (row: PricingSheetRow) => {
  const removalMonthPrice = countRemovalMonthPrice(
    Number(row?.rentalReadyPrice),
    Number(row?.depreciationPeriod),
    Number(row?.targetUtilRate) / 100,
  );

  if (row?.monthPrice && removalMonthPrice) {
    const priceCoefficintMonth = countPriceCoefficient(
      row?.monthPrice / PRICE_FACTOR,
      removalMonthPrice,
    );
    return priceCoefficintMonth;
  }
  return null;
};
